import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LayoutGrid, UserRound, LogOut } from "lucide-react";
import { fetchWithAuth } from "../utils/fetchWithAuth";

/* ------------------------------------------------------------------ */
/*  Persistent account avatar + menu, carried across all logged-in     */
/*  screens (hub, dashboards, and the public nav when signed in).      */
/*  Self-fetches the current user so it's a drop-in anywhere.          */
/* ------------------------------------------------------------------ */
export default function AccountMenu({ dark = false, size = 40 }) {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    fetchWithAuth("/api/accounts/me/")
      .then((d) => { if (alive) setMe(d); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const name = (me?.name || "").trim() || [me?.first_name, me?.last_name].filter(Boolean).join(" ").trim() || (me?.email ? me.email.split("@")[0] : "");
  const email = me?.email || "";
  const initials = (name || email || "?").split(/\s+/).map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase() || "?";
  const photo = me?.image_url || me?.profile_photo_url || null;
  // Personal profile editor (the avatar is the person, not the org).
  const editHref = "/dashboard/contractor/profile";

  const logout = async () => {
    try { await fetch("/api/accounts/logout/", { method: "POST", credentials: "include" }); } catch (_) {}
    ["authToken", "access", "refresh", "token", "bearer", "resumeToastShown"].forEach((k) => { try { localStorage.removeItem(k); } catch (_) {} });
    navigate("/auth/login");
  };

  const menuBg = dark ? "#161616" : "var(--surface)";
  const menuBorder = dark ? "rgba(255,255,255,.12)" : "var(--border)";
  const itemColor = dark ? "rgba(244,241,234,.92)" : "var(--ink)";
  const subColor = dark ? "rgba(244,241,234,.55)" : "var(--text-muted)";

  const item = {
    display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
    padding: "10px 14px", fontSize: "0.875rem", color: itemColor, textDecoration: "none",
    background: "transparent", border: "none", cursor: "pointer", fontFamily: "var(--font-sans, 'Instrument Sans', sans-serif)",
  };

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Account menu"
        style={{
          width: size, height: size, borderRadius: "50%", border: "none", cursor: "pointer", padding: 0,
          overflow: "hidden", display: "grid", placeItems: "center", fontWeight: 600, fontSize: size * 0.34,
          color: "var(--brand-ink)", background: photo ? "transparent" : "var(--brand-tint)",
          boxShadow: dark ? "0 0 0 2px rgba(255,255,255,.15)" : "0 0 0 2px var(--surface), 0 0 0 3px var(--border)",
        }}
      >
        {photo ? <img src={photo} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : initials}
      </button>

      {open && (
        <div
          role="menu"
          style={{
            position: "absolute", right: 0, top: "calc(100% + 10px)", zIndex: 60, width: 234,
            background: menuBg, border: `1px solid ${menuBorder}`, borderRadius: 14,
            boxShadow: "var(--shadow-lg, 0 22px 44px -24px rgba(0,0,0,.45))", overflow: "hidden", padding: "6px 0",
          }}
        >
          {(name || email) && (
            <div style={{ padding: "10px 14px 12px", borderBottom: `1px solid ${menuBorder}` }}>
              <div style={{ fontWeight: 600, fontSize: "0.875rem", color: itemColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{name || "Your account"}</div>
              {email && <div style={{ fontSize: "0.75rem", color: subColor, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{email}</div>}
            </div>
          )}
          <Link to={editHref} role="menuitem" style={item} onClick={() => setOpen(false)}
            onMouseEnter={(e) => (e.currentTarget.style.background = dark ? "rgba(255,255,255,.08)" : "var(--surface-sunken)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
            <UserRound size={16} /> Edit profile
          </Link>
          <Link to="/home" role="menuitem" style={item} onClick={() => setOpen(false)}
            onMouseEnter={(e) => (e.currentTarget.style.background = dark ? "rgba(255,255,255,.08)" : "var(--surface-sunken)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
            <LayoutGrid size={16} /> My Sift
          </Link>
          <div style={{ borderTop: `1px solid ${menuBorder}`, margin: "6px 0 0" }} />
          <button type="button" role="menuitem" onClick={logout} style={{ ...item, color: "#d8483a" }}
            onMouseEnter={(e) => (e.currentTarget.style.background = dark ? "rgba(216,72,58,.12)" : "rgba(216,72,58,.08)")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}>
            <LogOut size={16} /> Log out
          </button>
        </div>
      )}
    </div>
  );
}
